import React from "react";
import { useLocation } from "react-router-dom";
import { endpoints } from "config";
import DeleteModal from "components/helpers/DeleteModal";

// Icons
import View from "assets/Icon_Action_01.png";
import Update from "assets/Icon_Action_02.png";
import Delete from "assets/Icon_Action_03.png";

import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import { CardListPage } from "components/Category/Category";
import { parseListDate } from "helpers/parseListDate";
import WorkflowCreateEdit from "./WorkflowCreateEdit/WorkflowCreateEdit";
import WorkflowClonePlaceholder from "./WorkflowClonePlaceholder";
import { NestedFormData } from "components/Entity/Entity";

const Workflow = lazyWithRetry(() => import("./WorkflowConfiguration"));

// Define the url for the table's data endpoint.
const url = endpoints.workflowConfiguration.list;
const csvUrl = endpoints.workflowConfiguration.listCSV;

export type WorkflowListData = {
  workflows__ID: number;
  workflows__Name: string;
  workflows__Description: string;
  trigger__TriggerSummary: string;
  // actions__ActionSummary: string; // TODO - waiting for backend to confirm this field
  // approvals__ApprovalSummary: string; // TODO - waiting for backend to confirm this field
  workflows__TriggerTypes__Name: string;
  Programmes__Name: string;
  DocumentTypes__DocumentTypesName: string;
  workflows__bUniversal: boolean;
  workflows__bActive: boolean;
  workflows__AddDate: string;
  workflows__AddedBy: string;
  workflows__EditDate: string;
  workflows__LastEditedBy: string;
  workflowTriggerTypes__Name: string;
  actions__ActionsSummary: string;
  approvals__bApproval: boolean;
  approvalOutcomes__ApprovalOutcomesSummary: string;
};

// Define which column on the table represents each row's ID.
// This should be unique to each row.
export const idColumn = "workflows__ID";
const noun = "workflow";

const getCrudItems = (
  nestedFormData?: NestedFormData
): CrudItems<WorkflowListData> => {
  return [
    {
      icon: View,
      title: `Create ${noun}`,
      imgAltText: "Create",
      menuCode: "CREATE",
      showInMultiSelect: false,
      permissionsKey: "create",
      content: (onDone, selectedData, menu) => (
        <WorkflowCreateEdit
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="create"
          // submitButtonText="Add"
        />
      ),
      inRightPanel: true,
      isDisabled: false,
      notInMenus: true,
    },
    {
      icon: View,
      title: `View ${noun}`,
      imgAltText: "View",
      permissionsKey: "view",
      menuCode: "VIEW",
      showInMultiSelect: false,
      content: (
        onDone,
        selectedData,
        menu,
        additionalData,
        clickedIdIfMultiIds
      ) => (
        <Workflow
          onDone={onDone}
          selectedData={selectedData}
          idFromRowOption={menu.selectedId}
          actionType="view"
          submitButtonText="Close"
          hideActionButton={true}
          autoMenuActionsCode={"VIEW"}
          clickedIdIfMultiIds={clickedIdIfMultiIds}
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: Update,
      imgAltText: "Update",
      permissionsKey: "edit",
      title: `Update ${noun}`,
      menuCode: "UPDATE",
      showInMultiSelect: false,
      content: (onDone, selectedData, menu) => (
        <WorkflowCreateEdit
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="edit"
          // submitButtonText="Update"
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: Update,
      imgAltText: "Update",
      permissionsKey: "clone",
      title: `Clone ${noun}`,
      menuCode: "CLONE_WORKFLOW",
      showInMultiSelect: false,
      content: (onDone, selectedData, menu) => (
        <WorkflowClonePlaceholder
          selectedData={selectedData}
          onDone={onDone}
          idFromRowOption={menu.selectedId}
          actionType="edit"
        />
      ),
      inRightPanel: true,
      isDisabled: false,
    },
    {
      icon: Delete,
      imgAltText: "Delete",
      permissionsKey: "delete",
      title: data =>
        data.length <= 1 ? `Delete ${noun}` : `Delete ${data.length} ${noun}s`,
      menuCode: "DELETE",
      showInMultiSelect: true,
      content: (onActionComplete, selectedData, menu) => (
        <DeleteModal
          url={endpoints.workflowConfiguration.delete}
          noun={noun}
          selectedIds={selectedData.map(d => {
            return d.workflows__ID;
          })}
          renderListItem={data => {
            return <li>{data.workflows__Name}</li>;
          }}
          selectedData={selectedData}
          open={menu.currentMenu === "DELETE"}
          close={onActionComplete}
          idFromRowOption={menu.selectedId}
        />
      ),
      inRightPanel: false,
      isDisabled: false,
    },
  ];
};

// Optional - function for formatting a row by column name. Takes a row and should
// spit out an object with the same keys.
// Should do this for date columns and format them as required
const rowFormatter = (
  row: WorkflowListData
): { [K in keyof typeof row]: React.ReactNode } => {
  let formattedRow: any = { ...row };

  if (formattedRow.workflows__AddDate) {
    formattedRow = {
      ...formattedRow,
      workflows__AddDate: parseListDate(formattedRow.workflows__AddDate, true),
    };
  }

  if (formattedRow.workflows__EditDate) {
    formattedRow = {
      ...formattedRow,
      workflows__EditDate: parseListDate(
        formattedRow.workflows__EditDate,
        true
      ),
    };
  }

  return formattedRow;
};

export interface WorkflowConfigurationsProps {
  nestedFormData?: NestedFormData;
}

export type Props = WorkflowConfigurationsProps;

const WorkflowConfigurations: React.FC<Props> = props => {
  const location = useLocation();
  const crudItems = getCrudItems(props.nestedFormData);

  return (
    <div className="WorkflowConfigurationPage CardListPage">
      <Category<WorkflowListData>
        location={location}
        noun={noun}
        idColumn={idColumn}
        listEndpointUrl={url}
        defaultBActiveFilterColumnKey={"Workflows__bActive"}
        rowFormatter={rowFormatter}
        crudComponents={crudItems}
        listCSV={{ url: csvUrl, noun }}
        hiddenColumns={[idColumn]}
        // disableMenuItemData={{
        //   resendverificationlink: {
        //     fieldName: "siteUsers__bAccountVerified",
        //     fieldValue: true,
        //   },
        // }}
        defaultCardOrListView={CardListPage.Card}
        hideHeaderTitle={true}
        largeRightPanel={
          menuCode => menuCode !== "VIEW" // && menuCode !== "CLONE_WORKFLOW"
        }
        cardProps={{
          cardTitleColumn: "workflows__Name",
          includeTypeColumn: false,
          // cardTypeColumn: "",
          cardActionType: "edit",
          cardLayout: 1,
          cardCustomContent: data => {
            let noWorkflowData = false;

            if (
              // !data?.trigger__TriggerSummary &&
              !data?.workflowTriggerTypes__Name &&
              !data?.actions__ActionsSummary &&
              !data?.approvals__bApproval
            ) {
              noWorkflowData = true;
            }
            return (
              <div className="WorkflowConfigurationDetails">
                {data?.workflows__Description && (
                  <p>{data?.workflows__Description}</p>
                )}

                {noWorkflowData ? (
                  <div className="NoWorkflowConfigured">
                    No workflow configured
                  </div>
                ) : (
                  <ul className="WorkflowConfigurationDetailsList">
                    {/* {data?.trigger__TriggerSummary && (
                      <li>{data?.trigger__TriggerSummary}</li>
                    )} */}

                    {data?.workflowTriggerTypes__Name && (
                      <li>{`Trigger: ${data?.workflowTriggerTypes__Name}`}</li>
                    )}

                    {data?.actions__ActionsSummary && (
                      <li>
                        Actions:
                        <ul className="SubList">
                          {data?.actions__ActionsSummary
                            .split(",")
                            .map(item => {
                              return <li>{`${item}`}</li>;
                            })}
                        </ul>
                      </li>
                    )}

                    {data?.approvals__bApproval && (
                      <li>
                        {`Approval decision:`}

                        {data?.approvalOutcomes__ApprovalOutcomesSummary ? (
                          <ul className="SubList">
                            {data?.approvalOutcomes__ApprovalOutcomesSummary
                              .split(",")
                              .map(item => {
                                return <li>{`Approval outcome: ${item}`}</li>;
                              })}
                          </ul>
                        ) : (
                          <ul className="SubList">
                            <li>No approval outcomes data</li>
                          </ul>
                        )}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          },
          // cardFooterButtonText: "Update workflow",
          // cardContentClassname: "CardLayoutWorkflowContainer",
        }}
        rowCellOptions={{
          workflows__Description: { width: "Huge" },
          workflows__AddDate: { width: "Medium2" },
          workflows__AddedBy: { width: "Medium2" },
          workflows__EditDate: { width: "Medium2" },
          workflows__LastEditedBy: { width: "SemiLarge2" },
          trigger__TriggerSummary: { width: "SemiLarge2" },
        }}
        nestedFormData={props.nestedFormData}
        // maxCardPageTitleLength={30}
      />
    </div>
  );
};

export default WorkflowConfigurations;
